import React, { useState, useEffect } from 'react';
import { useAuthenticatedApi } from '../api';
import { useAuth0 } from '@auth0/auth0-react';

const ReactionButtons = ({ contentType, contentId, style = {}, onReactionChange, ownerId }) => {
  const { authenticatedFetch, csrfToken } = useAuthenticatedApi();
  const { isAuthenticated, user } = useAuth0();
  const [userReaction, setUserReaction] = useState(null);
  const [counts, setCounts] = useState({ 'thumbs-up': 0, 'heart': 0, 'thumbs-down': 0 });
  const [loading, setLoading] = useState({});
  const [currentUserMemberId, setCurrentUserMemberId] = useState(null);

  // Fetch current user's member ID
  useEffect(() => {
    const fetchCurrentUserMemberId = async () => {
      if (!isAuthenticated || !user?.email) return;
      
      try {
        const response = await authenticatedFetch(
          `${process.env.REACT_APP_API_URL || '/api'}/members/by-email?email=${encodeURIComponent(user.email)}`
        );
        if (response.ok) {
          const memberData = await response.json();
          setCurrentUserMemberId(memberData.id);
        }
      } catch (error) {
        console.error('Error fetching current user member ID:', error);
      }
    };

    fetchCurrentUserMemberId();
  }, [isAuthenticated, user, authenticatedFetch]);

  // Check reaction status on mount
  useEffect(() => {
    const checkReactionStatus = async () => {
      if (isAuthenticated) {
        try {
          const response = await authenticatedFetch(
            `/api/likes/check/${contentType}/${contentId}`
          );
          if (response.ok) {
            const data = await response.json();
            setUserReaction(data.reaction);
            setCounts(data.counts || { 'thumbs-up': 0, 'heart': 0, 'thumbs-down': 0 });
          }
        } catch (error) {
          console.error('Error checking reaction status:', error);
        }
      }
    };

    checkReactionStatus();
  }, [contentType, contentId, isAuthenticated, authenticatedFetch]);

  const handleReaction = async (reactionType) => {
    if (!isAuthenticated || loading[reactionType]) return;

    setLoading(prev => ({ ...prev, [reactionType]: true }));
    
    // Optimistic update
    const previousReaction = userReaction;
    const previousCounts = { ...counts };
    
    if (previousReaction === reactionType) {
      // Remove reaction
      setUserReaction(null);
      setCounts(prev => ({
        ...prev,
        [reactionType]: Math.max(0, prev[reactionType] - 1)
      }));
    } else if (previousReaction) {
      // Change reaction
      setUserReaction(reactionType);
      setCounts(prev => ({
        ...prev,
        [previousReaction]: Math.max(0, prev[previousReaction] - 1),
        [reactionType]: prev[reactionType] + 1
      }));
    } else {
      // Add new reaction
      setUserReaction(reactionType);
      setCounts(prev => ({
        ...prev,
        [reactionType]: prev[reactionType] + 1
      }));
    }

    try {
      const response = await authenticatedFetch('/api/likes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          content_type: contentType,
          content_id: contentId,
          reaction_type: reactionType
        })
      });

      if (response.ok) {
        const data = await response.json();
        setUserReaction(data.reaction);
        setCounts(data.counts || { 'thumbs-up': 0, 'heart': 0, 'thumbs-down': 0 });
        
        // Notify parent component of reaction change
        if (onReactionChange) {
          onReactionChange(data.counts);
        }
      } else {
        // Revert on error
        setUserReaction(previousReaction);
        setCounts(previousCounts);
      }
    } catch (error) {
      console.error('Error toggling reaction:', error);
      // Revert on error
      setUserReaction(previousReaction);
      setCounts(previousCounts);
    } finally {
      setLoading(prev => ({ ...prev, [reactionType]: false }));
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  // Check if current user owns this content
  const isOwner = ownerId && currentUserMemberId && ownerId === currentUserMemberId;

  const reactions = [
    { type: 'thumbs-up', emoji: '👍', label: 'Thumbs up' },
    { type: 'heart', emoji: '❤️', label: 'Love' },
    { type: 'thumbs-down', emoji: '👎', label: 'Thumbs down' }
  ];

  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '0.5rem',
      ...style 
    }}>
      {reactions.map(({ type, emoji, label }) => (
        <button
          key={type}
          onClick={() => handleReaction(type)}
          disabled={isOwner || loading[type]}
          title={isOwner ? 'You cannot react to your own content' : label}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0.375rem 0.625rem',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: userReaction === type ? '#e8f5e9' : '#f5f5f5',
            color: userReaction === type ? '#2e7d32' : '#666',
            cursor: isOwner || loading[type] ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            fontSize: style.fontSize || '0.875rem',
            opacity: isOwner ? 0.5 : (loading[type] ? 0.6 : 1)
          }}
          onMouseEnter={(e) => {
            if (!loading[type] && !isOwner) {
              e.target.style.backgroundColor = userReaction === type ? '#c8e6c9' : '#e0e0e0';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading[type]) {
              e.target.style.backgroundColor = userReaction === type ? '#e8f5e9' : '#f5f5f5';
            }
          }}
        >
          <span>{emoji}</span>
          <span style={{ fontWeight: '500' }}>{counts[type]}</span>
        </button>
      ))}
    </div>
  );
};

export default ReactionButtons;
